import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useCatalogueQuestions } from '../../hooks/useCatalogueQuestions';
import { NewPagination } from '../../components/Common/Pagination/Pagination';

interface CatalogueQuestionsProps {
  onItemCountChange?: (count: number) => void;
}

const CatalogueQuestions = forwardRef<any, CatalogueQuestionsProps>(({ onItemCountChange }, ref) => {
  const { 
    questions, 
    loading, 
    approveQuestion, 
    declineQuestion, 
    bulkApproveQuestions,
    pagination,
  } = useCatalogueQuestions();
  const [processingQuestionId, setProcessingQuestionId] = useState<string | null>(null);

  // Notify parent of item count changes
  useEffect(() => {
    if (onItemCountChange) {
      onItemCountChange(questions.length);
    }
  }, [questions.length, onItemCountChange]);

  const handleApprove = async (questionId: string) => {
    setProcessingQuestionId(questionId);
    
    await approveQuestion(questionId);

    setProcessingQuestionId(null);
  };

  const handleDecline = async (questionId: string) => {
    setProcessingQuestionId(questionId);
    
    await declineQuestion(questionId);

    setProcessingQuestionId(null);
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    handleBulkApprove: bulkApproveQuestions,
  }));

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)', flex: 1, overflow: 'hidden' }}>
      {/* Questions List Container */}
      {questions.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
            No pending questions to display.
          </Typography>
        </Box>
      ) : (<Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '0', 
        flex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingRight: 'var(--palette-light-4-value)', // 16px for scrollbar
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'var(--palette-light-text-tertiary-value-hex)',
          borderRadius: '4px',
        },
      }}>
        {questions.map((question) => (
          <Box
            key={question.questionId}
            sx={{
              display: 'flex',
              gap: 'var(--palette-light-10-value)', // 40px
              padding: 'var(--palette-light-6-value) 0', // 24px vertical padding
              borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)',
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)', flex: 1, minWidth: '250px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                  {formatDate(question.questionCreatedAt)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)' }}>
                <Box sx={{ display: 'flex', gap: 'var(--palette-light-1-value)' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {question.userName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 'var(--palette-light-1-value)' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    Vendor:
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-brand-primary-value-hex)' }}>
                    {question.vendorName}
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ 
                fontSize: 'var(--palette-light-font-size-default-value)', 
                color: 'var(--palette-light-text-primary-value-hex)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
                lineHeight: '24px',
                maxHeight: '72px',
                width: '100%',
              }}>
                {question.question}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 'var(--palette-light-2-value)', justifyContent: 'flex-end', alignItems: 'flex-start', minWidth: '300px' }}>
              <Button
                variant="contained"
                disabled={processingQuestionId === question.questionId}
                onClick={() => handleApprove(question.questionId)}
                sx={{
                  height: 'var(--palette-light-14-value)', // 56px
                  padding: '0 var(--palette-light-8-value)', // 0 32px
                  borderRadius: 'var(--palette-light-4-value)', // 16px
                  backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                  color: 'var(--palette-light-text-foreground-value-hex)',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                    opacity: 0.9,
                  },
                  '&:disabled': {
                    backgroundColor: 'var(--palette-light-text-tertiary-value-hex)',
                    color: 'var(--palette-light-text-foreground-value-hex)',
                  },
                }}
              >
                {processingQuestionId === question.questionId ? <CircularProgress size={20} color="inherit" /> : 'Approve'}
              </Button>
              <Button
                variant="outlined"
                disabled={processingQuestionId === question.questionId}
                onClick={() => handleDecline(question.questionId)}
                sx={{
                  height: 'var(--palette-light-14-value)', // 56px
                  padding: '0 var(--palette-light-8-value)', // 0 32px
                  borderRadius: 'var(--palette-light-4-value)', // 16px
                  borderColor: 'var(--palette-light-brand-primary-value-hex)',
                  color: 'var(--palette-light-brand-primary-value-hex)',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'var(--palette-light-brand-primary-value-hex)',
                    backgroundColor: 'rgba(103, 49, 79, 0.04)',
                  },
                  '&:disabled': {
                    borderColor: 'var(--palette-light-text-tertiary-value-hex)',
                    color: 'var(--palette-light-text-tertiary-value-hex)',
                  },
                }}
              >
                {processingQuestionId === question.questionId ? <CircularProgress size={20} color="inherit" /> : 'Decline'}
              </Button>
            </Box>
          </Box>
        ))}
      </Box>)}

      <NewPagination
        {...pagination}
      />
    </Box>
  );
});

CatalogueQuestions.displayName = 'CatalogueQuestions';

export default CatalogueQuestions;
